// components/InventoryDataGrid.vue
<template>
    <div class="card border-0 shadow-sm mb-3">
        <div class="card-body p-4">
            <!-- Header -->
            <div class="d-flex justify-content-between align-items-center mb-3">
                <h5 class="card-title mb-0 text-muted fw-normal">{{ title }}</h5>
                <div class="d-flex align-items-center">
                    <span class="badge bg-primary me-2">{{ selectedItems.length }}</span>
                    <small class="text-muted">Selected</small>
                </div>
            </div>

            <!-- Search and Controls -->
            <div class="row g-2 mb-3">
                <div class="col-lg-6">
                    <div class="input-group">
                        <span class="input-group-text bg-light border-end-0">
                            <i class="bi bi-search"></i>
                        </span>
                        <input 
                            type="text" 
                            v-model="searchQuery" 
                            :placeholder="searchPlaceholder" 
                            class="form-control border-start-0"
                            @input="onSearchInput"
                        />
                    </div>
                </div>
                
                <div class="col-lg-3" v-if="showItemsPerPageSelector">
                    <select 
                        v-model.number="itemsPerPage" 
                        @change="onItemsPerPageChange" 
                        class="form-select"
                    >
                        <option :value="10">Show 10</option>
                        <option :value="20">Show 20</option>
                        <option :value="50">Show 50</option>
                        <option :value="100">Show 100</option>
                    </select>
                </div>
                
                <div class="col-lg-3" v-if="showBulkActions">
                    <div class="btn-group w-100" role="group">
                        <button 
                            @click="selectAllVisible" 
                            class="btn btn-outline-primary btn-sm"
                            :disabled="paginatedItems.length === 0"
                            type="button"
                        >
                            Select All
                        </button>
                        <button 
                            @click="clearSelection" 
                            class="btn btn-outline-secondary btn-sm"
                            :disabled="selectedItems.length === 0"
                            type="button"
                        >
                            Clear
                        </button>
                    </div>
                </div>
            </div>

            <!-- Custom Filters Slot -->
            <div v-if="$slots.filters" class="row g-2 mb-3">
                <slot 
                    name="filters" 
                    :filteredItems="serverPagination ? items : filteredItems"
                    :resetPagination="resetPagination"
                    :clearSelection="clearSelection"
                    :refresh="refresh"
                />
            </div>

            <!-- Loading State -->
            <div v-if="isLoading" class="text-center py-5">
                <div class="spinner-border text-primary" role="status">
                    <span class="visually-hidden">Loading...</span>
                </div>
                <p class="mt-2 text-muted">{{ loadingText }}</p>
            </div>

            <!-- Error State -->
            <div v-else-if="error" class="text-center py-5">
                <i class="bi bi-exclamation-triangle display-4 text-danger mb-3"></i>
                <h6 class="text-danger">{{ error }}</h6>
                <button @click="refresh" class="btn btn-outline-primary mt-2" type="button">
                    <i class="bi bi-arrow-clockwise me-1"></i>
                    Retry
                </button>
            </div>

            <!-- Data Grid -->
            <div v-else-if="paginatedItems.length > 0">
                <div class="row g-2">
                    <div 
                        v-for="item in paginatedItems" 
                        :key="getItemKey(item)"
                        class="col-lg-3 col-md-4 col-sm-6"
                    >
                        <div 
                            class="border rounded p-3 cursor-pointer h-100 position-relative"
                            :class="getItemClasses(item)"
                            @click="toggleSelection(getItemKey(item))"
                        >
                            <div class="d-flex align-items-start">
                                <div class="form-check me-2" v-if="showCheckboxes">
                                    <input 
                                        type="checkbox" 
                                        :id="`item-${getItemKey(item)}`"
                                        :checked="isSelected(getItemKey(item))"
                                        @click.stop
                                        @change="toggleSelection(getItemKey(item))"
                                        class="form-check-input"
                                    />
                                </div>
                                
                                <div class="flex-grow-1">
                                    <slot name="item" 
                                        :item="item" 
                                        :isSelected="isSelected(getItemKey(item))"
                                        :toggleSelection="() => toggleSelection(getItemKey(item))">
                                        <!-- Default Item Template -->
                                        <h6 class="mb-1 fw-medium small font-monospace">
                                            {{ getItemDisplay(item) }}
                                        </h6>
                                        <div class="text-muted small text-capitalize" 
                                             :class="{ 'text-light opacity-75': isSelected(getItemKey(item)) }">
                                            {{ item.type || item.category || 'N/A' }}
                                        </div>
                                        <div class="mt-2 d-flex justify-content-between align-items-center">
                                            <span class="badge bg-success text-light" style="font-size: 0.65rem;">
                                                {{ item.status || 'Available' }}
                                            </span>
                                            <button 
                                                class="btn btn-sm btn-outline-secondary py-0 px-1" 
                                                :class="{ 'btn-outline-light': isSelected(getItemKey(item)) }"
                                                @click.stop="handleItemAction(item)"
                                                title="View Details"
                                                type="button"
                                            >
                                                <i class="bi bi-eye" style="font-size: 0.75rem;"></i>
                                            </button>
                                        </div>
                                    </slot>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                <!-- Pagination -->
                <div class="d-flex justify-content-between align-items-center mt-3">
                    <div class="text-muted small">
                        Showing {{ paginationStart }} to {{ paginationEnd }} of {{ totalItems }} items
                        <slot name="pagination-info" 
                            :start="paginationStart" 
                            :end="paginationEnd" 
                            :total="totalItems"
                        />
                    </div>
                    
                    <nav v-if="totalPages > 1 && showPagination">
                        <ul class="pagination pagination-sm mb-0">
                            <li class="page-item" :class="{ disabled: currentPage === 1 }">
                                <button 
                                    class="page-link" 
                                    @click="goToPage(1)" 
                                    :disabled="currentPage === 1"
                                    type="button"
                                >
                                    First
                                </button>
                            </li>
                            <li class="page-item" :class="{ disabled: currentPage === 1 }">
                                <button 
                                    class="page-link" 
                                    @click="goToPage(currentPage - 1)" 
                                    :disabled="currentPage === 1"
                                    type="button"
                                >
                                    Previous
                                </button>
                            </li>
                            
                            <li 
                                v-for="page in visiblePages" 
                                :key="`page-${page}`"
                                class="page-item" 
                                :class="{ active: page === currentPage }"
                            >
                                <button 
                                    class="page-link" 
                                    @click="goToPage(page)"
                                    type="button"
                                >
                                    {{ page }}
                                </button>
                            </li>
                            
                            <li class="page-item" :class="{ disabled: currentPage === totalPages }">
                                <button 
                                    class="page-link" 
                                    @click="goToPage(currentPage + 1)" 
                                    :disabled="currentPage === totalPages"
                                    type="button"
                                >
                                    Next
                                </button>
                            </li>
                            <li class="page-item" :class="{ disabled: currentPage === totalPages }">
                                <button 
                                    class="page-link" 
                                    @click="goToPage(totalPages)" 
                                    :disabled="currentPage === totalPages"
                                    type="button"
                                >
                                    Last
                                </button>
                            </li>
                        </ul>
                    </nav>
                </div>
            </div>

            <!-- Empty State -->
            <div v-else class="text-center py-5">
                <slot name="empty-state">
                    <i class="bi bi-inbox display-4 text-muted mb-3"></i>
                    <h6 class="text-muted">{{ emptyStateTitle }}</h6>
                    <p class="small text-muted mb-0">{{ emptyStateMessage }}</p>
                </slot>
            </div>
        </div>
    </div>
</template>

<script lang="ts">
import { Component, Prop, Vue, Watch, toNative } from 'vue-facing-decorator'
import type { AxiosResponse, AxiosError } from 'axios'
import { debounce } from 'lodash'
import type { 
    DataGridItem, 
    DataGridResponse, 
    SimpleDataGridResponse, 
    DataGridFilters, 
    DataGridParams 
} from '@/models/DataGrid'

@Component({
    emits: [
        'update:selectedItems', 
        'itemAction', 
        'dataLoaded', 
        'error', 
        'loading',
        'searchChanged',
        'filtersChanged'
    ]
})
class DataGrid<T extends DataGridItem = DataGridItem> extends Vue {
    // Props with TypeScript types
    @Prop({ type: String, required: true })
    readonly url!: string

    @Prop({ type: String, default: 'Select Items' })
    readonly title!: string

    @Prop({ type: String, default: 'id' })
    readonly itemKey!: keyof T

    @Prop({ type: String, default: 'name' })
    readonly displayField!: keyof T

    @Prop({ type: Array, default: () => [] })
    readonly selectedItems!: (string | number)[]

    @Prop({ type: String, default: 'Search items...' })
    readonly searchPlaceholder!: string

    @Prop({ type: String, default: 'Loading items...' })
    readonly loadingText!: string

    @Prop({ type: String, default: 'No items found' })
    readonly emptyStateTitle!: string

    @Prop({ type: String, default: 'Try adjusting your search criteria' })
    readonly emptyStateMessage!: string

    @Prop({ type: Boolean, default: true })
    readonly showCheckboxes!: boolean

    @Prop({ type: Boolean, default: true })
    readonly showBulkActions!: boolean

    @Prop({ type: Boolean, default: true })
    readonly showPagination!: boolean

    @Prop({ type: Boolean, default: true })
    readonly showItemsPerPageSelector!: boolean

    @Prop({ type: Number, default: 20 })
    readonly defaultItemsPerPage!: number

    @Prop({ type: Object as () => DataGridFilters, default: () => ({}) })
    readonly filters!: DataGridFilters

    @Prop({ type: Object as () => Record<string, any>, default: () => ({}) })
    readonly additionalParams!: Record<string, any>

    @Prop({ type: Boolean, default: false })
    readonly autoLoad!: boolean

    @Prop({ type: Number, default: 300 })
    readonly searchDebounce!: number

    // Data with proper typing
    items: T[] = []
    searchQuery = ''
    itemsPerPage = this.defaultItemsPerPage
    currentPage = 1
    isLoading = false
    error: string | null = null
    serverPagination = false
    totalItems = 0
    totalPages = 1
    lastRequestId = 0

    // Debounced search function
    private debouncedSearch = debounce(this.performSearch, this.searchDebounce)

    // Computed properties with proper typing
    get filteredItems(): T[] {
        if (this.serverPagination) {
            return this.items
        }

        let filtered = [...this.items]

        // Client-side search
        if (this.searchQuery.trim()) {
            const query = this.searchQuery.toLowerCase().trim()
            filtered = filtered.filter((item: T) => 
                Object.values(item).some(value => 
                    String(value).toLowerCase().includes(query)
                )
            )
        }

        // Client-side filtering
        Object.entries(this.filters).forEach(([key, value]) => {
            if (value !== null && value !== undefined && value !== '') {
                filtered = filtered.filter((item: T) => {
                    const itemValue = item[key as keyof T]
                    if (typeof value === 'string') {
                        return String(itemValue).toLowerCase().includes(value.toLowerCase())
                    }
                    return itemValue === value
                })
            }
        })

        return filtered
    }

    get paginatedItems(): T[] {
        if (this.serverPagination) {
            return this.items
        }
        
        const start = (this.currentPage - 1) * this.itemsPerPage
        const end = start + this.itemsPerPage
        return this.filteredItems.slice(start, end)
    }

    get paginationStart(): number {
        if (this.totalItems === 0) return 0
        return (this.currentPage - 1) * this.itemsPerPage + 1
    }

    get paginationEnd(): number {
        if (this.totalItems === 0) return 0
        return Math.min(this.currentPage * this.itemsPerPage, this.totalItems)
    }

    get visiblePages(): number[] {
        const pages: number[] = []
        const maxVisible = 5
        let start = Math.max(1, this.currentPage - Math.floor(maxVisible / 2))
        let end = Math.min(this.totalPages, start + maxVisible - 1)
        
        if (end - start < maxVisible - 1) {
            start = Math.max(1, end - maxVisible + 1)
        }
        
        for (let i = start; i <= end; i++) {
            pages.push(i)
        }
        
        return pages
    }

    // Watchers with proper typing
    @Watch('searchQuery')
    onSearchQueryChanged(newValue: string, oldValue: string): void {
        if (newValue !== oldValue) {
            this.debouncedSearch()
            this.$emit('searchChanged', newValue)
        }
    }

    @Watch('filters', { deep: true })
    onFiltersChanged(newFilters: DataGridFilters, oldFilters: DataGridFilters): void {
        if (JSON.stringify(newFilters) !== JSON.stringify(oldFilters)) {
            this.resetPagination()
            if (this.serverPagination) {
                this.loadData()
            }
            this.$emit('filtersChanged', newFilters)
        }
    }

    @Watch('itemsPerPage')
    onItemsPerPageChanged(): void {
        this.resetPagination()
        if (this.serverPagination) {
            this.loadData()
        }
    }

    @Watch('currentPage')
    onCurrentPageChanged(): void {
        if (this.serverPagination) {
            this.loadData()
        }
    }

    // Methods with proper typing
    async loadData(): Promise<void> {
        try {
            this.isLoading = true
            this.error = null
            this.$emit('loading', true)
            
            const requestId = ++this.lastRequestId
            const params: DataGridParams = {
                page: this.currentPage,
                per_page: this.itemsPerPage,
                search: this.searchQuery || undefined,
                ...this.filters,
                ...this.additionalParams
            }

            // Clean undefined values
            Object.keys(params).forEach(key => {
                if (params[key] === undefined || params[key] === '') {
                    delete params[key]
                }
            })

            const response: AxiosResponse<DataGridResponse<T> | SimpleDataGridResponse<T>> = 
                await this.$http.get(this.url, { params })
            
            // Ignore outdated requests
            if (requestId !== this.lastRequestId) {
                return
            }

            if (response.data) {
                // Check response structure
                if ('data' in response.data && 'current_page' in response.data) {
                    // Laravel pagination response
                    const paginatedResponse = response.data as DataGridResponse<T>
                    this.serverPagination = true
                    this.items = paginatedResponse.data
                    this.currentPage = paginatedResponse.current_page
                    this.totalPages = paginatedResponse.last_page
                    this.totalItems = paginatedResponse.total
                    this.itemsPerPage = paginatedResponse.per_page
                } else if ('results' in response.data) {
                    // Simple response
                    const simpleResponse = response.data as SimpleDataGridResponse<T>
                    this.items = simpleResponse.results
                    this.serverPagination = false
                    this.totalItems = simpleResponse.total
                    this.totalPages = Math.ceil(this.totalItems / this.itemsPerPage)
                } else if (Array.isArray(response.data)) {
                    // Direct array response
                    this.items = response.data as T[]
                    this.serverPagination = false
                    this.totalItems = this.items.length
                    this.totalPages = Math.ceil(this.totalItems / this.itemsPerPage)
                }

                this.$emit('dataLoaded', {
                    items: this.items,
                    total: this.totalItems,
                    serverPagination: this.serverPagination
                })
            }
        } catch (e: unknown) {
            const axiosError = e as AxiosError
            this.error = axiosError.response?.data?.message || 'Failed to load data'
            this.items = []
            this.$emit('error', axiosError)
            console.error('DataGrid load error:', axiosError)
        } finally {
            this.isLoading = false
            this.$emit('loading', false)
        }
    }

    private performSearch(): void {
        this.resetPagination()
        if (this.serverPagination) {
            this.loadData()
        }
    }

    // Selection methods
    isSelected(itemKey: string | number): boolean {
        return this.selectedItems.includes(itemKey)
    }

    toggleSelection(itemKey: string | number): void {
        const newSelection = [...this.selectedItems]
        const index = newSelection.indexOf(itemKey)
        
        if (index > -1) {
            newSelection.splice(index, 1)
        } else {
            newSelection.push(itemKey)
        }
        
        this.$emit('update:selectedItems', newSelection)
    }

    selectAllVisible(): void {
        const visibleIds = this.paginatedItems.map(item => this.getItemKey(item))
        const newSelection = [...this.selectedItems]
        
        visibleIds.forEach(id => {
            if (!newSelection.includes(id)) {
                newSelection.push(id)
            }
        })
        
        this.$emit('update:selectedItems', newSelection)
    }

    clearSelection(): void {
        this.$emit('update:selectedItems', [])
    }

    // Navigation methods
    resetPagination(): void {
        this.currentPage = 1
    }

    goToPage(page: number): void {
        if (page >= 1 && page <= this.totalPages && page !== this.currentPage) {
            this.currentPage = page
        }
    }

    // Event handlers
    onSearchInput(): void {
        // Search is handled by watcher with debouncing
    }

    onItemsPerPageChange(): void {
        // Handled by watcher
    }

    handleItemAction(item: T): void {
        this.$emit('itemAction', item)
    }

    // Utility methods
    getItemKey(item: T): string | number {
        return item[this.itemKey] as string | number
    }

    getItemDisplay(item: T): string {
        return String(item[this.displayField] || item[this.itemKey] || 'N/A')
    }

    getItemClasses(item: T): Record<string, boolean> {
        const isSelected = this.isSelected(this.getItemKey(item))
        return {
            ' text-light border-secondary border-2 text-dark': isSelected,
            'border-default': !isSelected,
            'item-hover': !isSelected
        }
    }

    // Public API methods
    refresh(): void {
        this.loadData()
    }

    reload(): void {
        this.resetPagination()
        this.searchQuery = ''
        this.loadData()
    }

    // Lifecycle
    mounted(): void {
        if (this.autoLoad) {
            this.loadData()
        }
    }

    beforeUnmount(): void {
        this.debouncedSearch.cancel()
    }
}

export default toNative(DataGrid)
</script>

<style scoped>
.cursor-pointer {
    cursor: pointer;
}

.font-monospace {
    font-family: 'SFMono-Regular', Consolas, 'Liberation Mono', Menlo, Courier, monospace;
}

.item-hover:hover {
    box-shadow: 0 0 0 0.2rem rgba(13, 110, 253, 0.25);
    transform: translateY(-1px);
    transition: all 0.15s ease-in-out;
}

.form-control:focus,
.form-select:focus {
    border-color: #86b7fe;
    box-shadow: 0 0 0 0.25rem rgba(13, 110, 253, 0.25);
}

.fade-enter-active,
.fade-leave-active {
    transition: opacity 0.15s ease;
}

.fade-enter-from,
.fade-leave-to {
    opacity: 0;
}
</style>